import { useEffect, useState } from "react";
import { format, isToday, isTomorrow, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { Calendar, Clock, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useWorkspace } from "@/hooks/useWorkspace";

interface Appointment {
  id: string;
  title: string;
  scheduled_date: string;
  scheduled_time: string;
  status: string;
  appointment_type: string;
  client?: { name: string } | null;
}

const statusColors: Record<string, string> = {
  scheduled: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  confirmed: "bg-green-500/10 text-green-500 border-green-500/20",
  completed: "bg-muted text-muted-foreground border-muted",
  cancelled: "bg-destructive/10 text-destructive border-destructive/20",
};

const statusLabels: Record<string, string> = {
  scheduled: "Programada",
  confirmed: "Confirmada",
  completed: "Completada",
  cancelled: "Cancelada",
};

export function UpcomingAppointments() {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { workspace } = useWorkspace();
  const workspaceId = workspace?.id || null;

  useEffect(() => {
    if (workspaceId) fetchAppointments();
  }, [workspaceId]);

  const fetchAppointments = async () => {
    if (!workspaceId) return;
    try {
      const today = new Date().toISOString().split("T")[0];
      const { data, error } = await supabase
        .from("appointments")
        .select(`id, title, scheduled_date, scheduled_time, status, appointment_type, clients:client_id (name)`)
        .eq("workspace_id", workspaceId)
        .gte("scheduled_date", today)
        .neq("status", "cancelled")
        .neq("status", "completed")
        .order("scheduled_date", { ascending: true })
        .order("scheduled_time", { ascending: true })
        .limit(5);

      if (error) throw error;
      const formattedData = (data || []).map(item => ({ ...item, client: item.clients as { name: string } | null }));
      setAppointments(formattedData);
    } catch (error) {
      console.error("Error fetching appointments:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return "Hoy";
    if (isTomorrow(date)) return "Mañana";
    return format(date, "EEE, d MMM", { locale: es });
  };

  const formatTime = (timeStr: string) => timeStr.slice(0, 5);

  if (loading) {
    return (
      <Card className="animate-fade-in">
        <CardHeader><CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5 text-primary" /> Próximas Citas</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-4"><Skeleton className="h-12 w-12 rounded-lg" /><div className="flex-1 space-y-2"><Skeleton className="h-4 w-3/4" /><Skeleton className="h-3 w-1/2" /></div></div>
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="animate-fade-in">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5 text-primary" /> Próximas Citas</CardTitle>
        <button onClick={() => navigate("/crm/appointments")} className="text-sm text-primary hover:underline">Ver todas</button>
      </CardHeader>
      <CardContent>
        {appointments.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground"><Calendar className="h-10 w-10 mx-auto mb-2 opacity-50" /><p>No hay citas programadas</p></div>
        ) : (
          <div className="space-y-4">
            {appointments.map((appointment) => (
              <div key={appointment.id} className="flex items-start gap-4 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer" onClick={() => navigate("/crm/appointments")}>
                <div className="flex flex-col items-center justify-center h-12 w-12 rounded-lg bg-primary/10 text-primary"><span className="text-xs font-medium">{formatDate(appointment.scheduled_date)}</span></div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">{appointment.title}</p>
                  <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{formatTime(appointment.scheduled_time)}</span>
                    {appointment.client && <span className="flex items-center gap-1 truncate"><User className="h-3.5 w-3.5" />{appointment.client.name}</span>}
                  </div>
                </div>
                <Badge variant="outline" className={statusColors[appointment.status] || statusColors.scheduled}>{statusLabels[appointment.status] || appointment.status}</Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
